import { Component, computed, effect, inject, input, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { MatIcon } from "@angular/material/icon";
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { fadeInAnimation } from "@lib/base/animations/fade-in";
import { slideInAnimation } from "@lib/base/animations/slide";
import { TranslatePipe } from "@ngx-translate/core";
import { combineLatest, filter, map, startWith, switchMap } from "rxjs";
import { expandHeightAnimation } from "./sidebar.animations";
import { NavigationData, NavigationRoutes } from "./sidebar.component.types";
import { UserRolesService } from "@lib/core/services/user-roles.service";
import { AsyncPipe } from "@angular/common";

type NavigationAuthorized = {
    key: string;
    icon?: string;
    route?: string;
    permissions?: string[];
    children?: NavigationAuthorized[];
    authorized: boolean;
};

@Component({
    selector: "app-sidebar",
    imports: [
        RouterLink,
        MatIcon,
        TranslatePipe,
        AsyncPipe,
    ],
    templateUrl: "./sidebar.component.html",
    styleUrl: "./sidebar.component.scss",
    animations: [fadeInAnimation, slideInAnimation, expandHeightAnimation],
})
export class SidebarComponent {
    router = inject(Router);
    roleService = inject(UserRolesService);

    navigation = input.required<NavigationData>();

    selected = signal<string | undefined>(undefined);

    protected url = toSignal(this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => event.urlAfterRedirects),
        startWith(this.router.url),
    ));

    urlComputed = computed(() => {
        let url = this.url();
        if (url == null) return url;
        if (url.startsWith("/")) {
            url = url.substring(url.indexOf("/") + 1);
        }
        if (url && url.startsWith(this.navigation().settings.prefix)) {
            return url.replace(this.navigation().settings.prefix + "/", "");
        }
        return url;
    });

    isNavSelected = (nav: NavigationRoutes) => {
        if (nav.children && nav.children.length > 0) {
            return this.urlComputed() === nav.route;
        } else if (nav.route) {
            return this.urlComputed()?.includes(nav.route);
        } else {
            return false;
        }
    };

    effectUrl = effect(() => {
        const url = this.urlComputed();
        const res = this.navigation().routes.find((route) => {
            if (url == null) return false;
            const isChildMatching = route.children?.some(child => child?.route == null ? false : url.startsWith(child.route));
            return (route.route != null && route.route === url) || isChildMatching;
        });
        if (res?.key != null) {
            this.selected.set(res?.key);
        } else {
            this.selected.set(undefined);
        }
    });

    navigationPermissions = computed(() => {
        const navigation = this.navigation();
        const routes = navigation.routes;

        return this.getPermissions(routes);
    });

    navigationWithAuthorization = combineLatest({ permissions: toObservable(this.navigationPermissions) })
        .pipe(
            switchMap(({ permissions }) => this.roleService.canIAll("READ", permissions)),
            map(result => this.buildNavigationRoutes(this.navigation().routes, result)),
            map(result => result.filter(item => item.authorized)),
            map(result => this.removeUnauthorizedChildren(result)),
        );

    flattenNavigationRoutes(route: NavigationRoutes): NavigationRoutes[] {
        if (route == null) {
            return [];
        }

        const list: NavigationRoutes[] = [route];
        route.children?.forEach((child) => {
            list.push(...this.flattenNavigationRoutes(child));
        });

        return list;
    };

    buildNavigationRoutes(routes: NavigationRoutes[] | undefined, permissions: {
        permission: string;
        canAccess: boolean;
    }[]): NavigationAuthorized[] {
        if (routes == null) {
            return [];
        }

        return routes.map((route) => {
            const node = {
                ...route,
            } as NavigationAuthorized;
            node.children = this.buildNavigationRoutes(route.children, permissions);

            const someChildAuthorized
                = node.children == null
                    || node.children.length === 0
                    || node.children.some(child => child.authorized);

            node.authorized = permissions
                .filter(perm => route.permissions?.includes(perm.permission))
                .every(item => item.canAccess);
            node.authorized = node.authorized && someChildAuthorized;

            return node;
        });
    }

    private getPermissions(routes: NavigationRoutes[] | undefined): string[] {
        if (routes == null) {
            return [];
        }

        const permissions: string[] = [];
        routes.forEach((route) => {
            permissions.push(...route.permissions ?? []);
            permissions.push(...this.getPermissions(route.children));
        });

        return permissions;
    }

    private removeUnauthorizedChildren(routes: NavigationAuthorized[]): NavigationAuthorized[] {
        routes.forEach((route) => {
            route.children = route.children?.filter(child => child.authorized) ?? [];
        });
        return routes;
    }
}
